import { useState, useMemo } from 'react';
import { inferTableStructure } from '../../utils/tableInference';
import PreviewTable from './PreviewTable';

function LineChartMappingPanel({ columns, rows, initialMapping, onConfirm, onBack, ui }) {
  const numericColumns = columns.filter((column) => column.type === 'number');
  const stringColumns = columns.filter((column) => column.type === 'string');
  const inferred = useMemo(() => inferTableStructure(rows, columns, 'line-chart'), [rows, columns]);
  const initialMode = initialMapping.inputShape === 'wide' || Array.isArray(initialMapping.yColumns) ? 'wide' : 'long';
  const [mode, setMode] = useState(initialMode);
  const [mapping, setMapping] = useState(initialMapping);

  const xValue = mapping.x || numericColumns[0]?.name || '';
  const selectedYColumns = Array.isArray(mapping.yColumns) ? mapping.yColumns.filter((column) => column !== xValue) : [];
  const selectedY = mode === 'long' ? (mapping.y && mapping.y !== xValue ? mapping.y : '') : '';
  const selectedSeries = mapping.series || '';

  const canConfirm = mode === 'wide'
    ? Boolean(xValue) && selectedYColumns.length > 0
    : Boolean(xValue) && Boolean(selectedY);

  const toggleWideColumn = (columnName) => {
    setMapping((prev) => {
      const current = Array.isArray(prev.yColumns) ? prev.yColumns : [];
      return current.includes(columnName)
        ? { ...prev, yColumns: current.filter((name) => name !== columnName) }
        : { ...prev, yColumns: [...current, columnName], inputShape: 'wide', y: undefined, series: undefined };
    });
  };

  const handleConfirm = () => {
    if (mode === 'wide') {
      onConfirm({
        inputShape: 'wide',
        x: xValue,
        yColumns: selectedYColumns,
      });
      return;
    }

    onConfirm({
      inputShape: 'long',
      x: xValue,
      y: selectedY,
      ...(selectedSeries ? { series: selectedSeries } : {}),
    });
  };

  return (
    <div>
      {inferred?.notes && (
        <div className="border p-2.5 mb-3 text-xs leading-relaxed" style={{ color: ui.textSecondary, borderColor: ui.borderSecondary, backgroundColor: ui.bgSecondary }}>
          <span className="font-medium" style={{ color: ui.text }}>Auto-detected: </span>{inferred.notes}
        </div>
      )}
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <label className="text-xs" style={{ color: ui.text }}>
          <div className="mb-1 font-medium">X-axis column</div>
          <select
            value={xValue}
            onChange={(e) => {
              const nextX = e.target.value;
              setMapping((prev) => ({
                ...prev,
                x: nextX,
                y: prev.y === nextX ? '' : prev.y,
                yColumns: Array.isArray(prev.yColumns) ? prev.yColumns.filter((column) => column !== nextX) : [],
              }));
            }}
            className="w-full text-xs border py-1 px-2"
            style={{ borderColor: ui.borderSecondary }}
          >
            {numericColumns.map((column) => (
              <option key={column.name} value={column.name}>{column.name}</option>
            ))}
          </select>
        </label>
        <div className="text-xs" style={{ color: ui.text }}>
          <div className="mb-2 font-medium">How are your lines organized?</div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setMode('wide')}
              className="flex-1 border p-3 text-left transition-colors"
              style={{
                borderColor: mode === 'wide' ? ui.border : ui.borderSecondary,
                backgroundColor: mode === 'wide' ? ui.bgSecondary : 'transparent'
              }}
            >
              <div className="font-medium mb-2" style={{ color: ui.text }}>Multiple Value Columns</div>
              <div className="text-[10px] mb-2" style={{ color: ui.textSecondary }}>Each column becomes a separate line.</div>
              <table className="w-full text-[9px] border-collapse" style={{ borderColor: ui.borderSecondary }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: ui.borderSecondary }}>
                    <th className="text-left font-normal py-0.5">Date</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Model A</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Model B</th>
                  </tr>
                </thead>
                <tbody style={{ color: ui.textSecondary }}>
                  <tr>
                    <td className="py-0.5">Jan 1</td>
                    <td className="py-0.5">85%</td>
                    <td className="py-0.5">82%</td>
                  </tr>
                  <tr>
                    <td className="py-0.5">Jan 2</td>
                    <td className="py-0.5">86%</td>
                    <td className="py-0.5">83%</td>
                  </tr>
                </tbody>
              </table>
            </button>
            <button
              type="button"
              onClick={() => setMode('long')}
              className="flex-1 border p-3 text-left transition-colors"
              style={{
                borderColor: mode === 'long' ? ui.border : ui.borderSecondary,
                backgroundColor: mode === 'long' ? ui.bgSecondary : 'transparent'
              }}
            >
              <div className="font-medium mb-2" style={{ color: ui.text }}>Single Value Column</div>
              <div className="text-[10px] mb-2" style={{ color: ui.textSecondary }}>A category column groups the values into lines.</div>
              <table className="w-full text-[9px] border-collapse" style={{ borderColor: ui.borderSecondary }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: ui.borderSecondary }}>
                    <th className="text-left font-normal py-0.5">Date</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Model</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Score</th>
                  </tr>
                </thead>
                <tbody style={{ color: ui.textSecondary }}>
                  <tr>
                    <td className="py-0.5">Jan 1</td>
                    <td className="py-0.5">Model A</td>
                    <td className="py-0.5">85%</td>
                  </tr>
                  <tr>
                    <td className="py-0.5">Jan 1</td>
                    <td className="py-0.5">Model B</td>
                    <td className="py-0.5">82%</td>
                  </tr>
                </tbody>
              </table>
            </button>
          </div>
        </div>
      </div>

      {mode === 'wide' ? (
        <div className="border p-3 mb-4" style={{ borderColor: ui.borderSecondary }}>
          <div className="text-xs font-medium mb-2" style={{ color: ui.text }}>Select columns to plot as separate lines</div>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {numericColumns.filter((column) => column.name !== xValue).map((column) => (
              <label key={column.name} className="flex items-center gap-2 text-xs" style={{ color: ui.text }}>
                <input
                  type="checkbox"
                  checked={selectedYColumns.includes(column.name)}
                  onChange={() => toggleWideColumn(column.name)}
                />
                <span>{column.name}</span>
              </label>
            ))}
          </div>
          {selectedYColumns.length === 0 && (
            <p className="text-xs mt-2" style={{ color: ui.textSecondary }}>
              Select one or more numeric columns to plot as separate lines.
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <label className="text-xs" style={{ color: ui.text }}>
            <div className="mb-1 font-medium">Value column (Y-axis)</div>
            <select
              value={selectedY}
              onChange={(e) => setMapping((prev) => ({ ...prev, y: e.target.value, inputShape: 'long', yColumns: undefined }))}
              className="w-full text-xs border py-1 px-2"
              style={{ borderColor: ui.borderSecondary }}
            >
              <option value="">Select a value column</option>
              {numericColumns.filter((column) => column.name !== xValue).map((column) => (
                <option key={column.name} value={column.name}>{column.name}</option>
              ))}
            </select>
          </label>
          <label className="text-xs" style={{ color: ui.text }}>
            <div className="mb-1 font-medium">Split lines by (optional)</div>
            <select
              value={selectedSeries}
              onChange={(e) => setMapping((prev) => ({ ...prev, series: e.target.value }))}
              className="w-full text-xs border py-1 px-2"
              style={{ borderColor: ui.borderSecondary }}
            >
              <option value="">None</option>
              {stringColumns.map((column) => (
                <option key={column.name} value={column.name}>{column.name}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <PreviewTable columns={columns} rows={rows} ui={ui} />

      <div className="flex gap-2">
        <button
          onClick={onBack}
          className="flex-1 text-xs border py-2 ui-hover ui-border"
          style={{ borderColor: ui.border, color: ui.text }}
        >
          &larr; Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={!canConfirm}
          className={`flex-1 text-xs border py-2 font-medium ui-border ${
            !canConfirm ? 'opacity-40 cursor-not-allowed' : 'ui-hover'
          }`}
          style={{ borderColor: ui.border, color: ui.text }}
        >
          Confirm &rarr;
        </button>
      </div>
    </div>
  );
}

export default LineChartMappingPanel;
